// Handle updates after a heartbeat mutation.

import { HeartbeatMutationOptions } from "@pathwright/graphql-client/src/generated"
import { lastHeartbeatVar, getHeartbeat } from "./heartbeat"

export const handleChangeInActiveSubscriptions: NonNullable<
  HeartbeatMutationOptions["update"]
> = (cache, { data }) => {
  // Modify the cached subscriptions to reflect the new heartbeat,
  // "cancelling" subscriptions that are no longer active
  // and "un-cancelling" subscriptions that are now active.
  const prevActiveSubscriptionIds = lastHeartbeatVar()?.active_subscription_ids
  const currentActiveSubscriptionIds = getHeartbeat(
    data?.heartbeat
  )?.active_subscription_ids

  // If the previous or current active subscription ids are not set, then do not modify the cached subscriptions.
  if (!prevActiveSubscriptionIds || !currentActiveSubscriptionIds) {
    return
  }

  const removedSubscriptionIds = prevActiveSubscriptionIds.filter(
    (id) => !currentActiveSubscriptionIds.includes(id)
  )
  const addedSubscriptionIds = currentActiveSubscriptionIds.filter(
    (id) => !prevActiveSubscriptionIds.includes(id)
  )

  removedSubscriptionIds.forEach((id) => {
    cache.modify({
      id: cache.identify({
        __typename: "Subscription",
        id
      }),
      fields: {
        cancellation_reason: () => 0, // user unsubscribed
        canceled_dtime: () => new Date().toISOString(),
        is_canceled_at_cycle_end: () => false
      }
    })
  })

  addedSubscriptionIds.forEach((id) => {
    cache.modify({
      id: cache.identify({
        __typename: "Subscription",
        id
      }),
      fields: {
        cancellation_reason: () => null,
        canceled_dtime: () => null,
        is_canceled_at_cycle_end: () => false
      }
    })
  })

  // Legacy integration: refetch school subscription in subscribe store.
  if (removedSubscriptionIds.length > 0 || addedSubscriptionIds.length > 0) {
    window.App?.getStore("subscribe")?.action?.reload?.()
  }
}

export const handleChangeInMembershipStatus: NonNullable<
  HeartbeatMutationOptions["update"]
> = (cache, { data }) => {
  // Modify the cached membership revoked status to reflect the new heartbeat.
  const prevMembership = lastHeartbeatVar()?.membership
  const currentMembership = getHeartbeat(data?.heartbeat)?.membership

  if (!prevMembership || !currentMembership) {
    return
  }

  // If user's access to the school has changed, reload the page, allowing backend to redirect the user if needed and client to update the UI.
  if (
    !prevMembership.access_revoked_time !==
      !currentMembership.access_revoked_time ||
    !prevMembership.archived_time !== !currentMembership.archived_time
  ) {
    window.location.reload()
  }
}

const handleUpdateHeartbeat: NonNullable<HeartbeatMutationOptions["update"]> = (
  ...args
) => {
  handleChangeInActiveSubscriptions(...args)
  handleChangeInMembershipStatus(...args)
}

export default handleUpdateHeartbeat
